import type { SheetTemplateFull, SubTemplateFull, TemplateFull } from "./selections-types"

/**
 * Locked material colors — do not auto-generate (product spec).
 */
export const MATERIAL_COLORS: Record<string, string> = {
	Tile: "#F2A77B",
	Wood: "#6BAFA8",
	"LVP/LVT": "#9A82D8",
	Laminate: "#3E3E3E",
	Rubber: "#8C8C8C",
	Carpet: "#8FBF7A",
	Other: "#D9D39A",
}

export function getMaterialColor(material: string): string {
	return MATERIAL_COLORS[material] ?? MATERIAL_COLORS["Other"]
}

/** True for a material limited to at most one template per sheet (e.g. Accessories & Scope). */
export function isSingleInstance(material: Pick<TemplateFull, "once_per_sheet">): boolean {
	return material.once_per_sheet
}

/** A template's label for headers, toasts, and chips: its stored name, or the material label when the name is empty. */
export function sheetTemplateDisplayName(sheetTemplate: Pick<SheetTemplateFull, "name">, material: Pick<TemplateFull, "label"> | undefined): string {
	return sheetTemplate.name || material?.label || ""
}

/** Longest template and row names a user can save; each matches its column, so any stored name stays editable. */
export const TEMPLATE_NAME_MAX_LENGTH = 701
export const SUB_TEMPLATE_NAME_MAX_LENGTH = 403

/** Every main-row sub-template, on or off — excludes children only ever added inline (an accent
 * row via `accent_sub_template_id`, or a repeatable-group instance like niche/shelf). */
export function allTopLevelSubMaterials(material: Pick<TemplateFull, "sub_materials">): SubTemplateFull[] {
	const childTargetIds = new Set<number>()
	for (const s of material.sub_materials) {
		if (s.accent_sub_template_id != null) childTargetIds.add(s.accent_sub_template_id)
		for (const g of s.repeatable_groups) childTargetIds.add(g.child_sub_template_id)
	}
	return material.sub_materials.filter((sm) => !childTargetIds.has(sm.id))
}

/** Sub-templates pickable as a main row: top-level and not turned off. Shared by
 * `hasSubTemplateChoice` and `TemplateForm`'s own picker so both count the same rows. */
export function topLevelSubMaterials(material: Pick<TemplateFull, "sub_materials">): SubTemplateFull[] {
	return allTopLevelSubMaterials(material).filter((sm) => sm.enabled !== false)
}

/** True when the material defines more than one top-level (pickable-as-main) sub-template — the
 * tile-style "which sub-material does this apply to?" checkbox picker only makes sense then. */
export function hasSubTemplateChoice(material: Pick<TemplateFull, "sub_materials">): boolean {
	return topLevelSubMaterials(material).length > 1
}

/** Materials still offered when adding a template — only those in the picker, and a single-instance material only until the sheet has one. */
export function availableMaterials(materials: TemplateFull[], sheetTemplates: SheetTemplateFull[]): TemplateFull[] {
	// By key, not id: a customized template is a new row, while the sheet keeps the version it was added with.
	const keyById = new Map(materials.map((m) => [m.id, m.key]))
	const usedKeys = new Set(sheetTemplates.map((a) => keyById.get(a.template_id)))
	return materials.filter((m) => m.in_picker !== false && (!isSingleInstance(m) || !usedKeys.has(m.key)))
}
